import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { submitReport, hasUserReported, countTodayReports } from '../lib/supabase';

const REASONS = [
  { id: 'inappropriate', label: 'Inappropriate content',   desc: 'Explicit, offensive, or adult content' },
  { id: 'spam',          label: 'Spam',                    desc: 'Repetitive, low-quality, or promotional' },
  { id: 'harmful',       label: 'Harmful / dangerous',     desc: 'Promotes harm, jailbreaks, or dangerous info' },
  { id: 'copyright',     label: 'Copyright violation',     desc: 'Stolen prompt or plagiarised content' },
  { id: 'other',         label: 'Other',                   desc: 'Something else not listed above' },
];

const DAILY_LIMIT = 10;
const AUTO_HIDE_THRESHOLD = 5;

export default function ReportModal({ entry, onClose, onReported }) {
  const { user } = useAuth();
  const [reason, setReason]   = useState('');
  const [details, setDetails] = useState('');
  const [step, setStep]       = useState('pick'); // pick | confirm | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) return;
    setSubmitting(true);

    try {
      // Check daily limit
      const todayCount = await countTodayReports(user.id);
      if (todayCount >= DAILY_LIMIT) {
        setErrorMsg(`You've reached the daily report limit (${DAILY_LIMIT}/day). Try again tomorrow.`);
        setStep('error');
        setSubmitting(false);
        return;
      }

      // Check already reported
      const alreadyReported = await hasUserReported(entry.id, user.id);
      if (alreadyReported) {
        setErrorMsg('You have already reported this entry.');
        setStep('error');
        setSubmitting(false);
        return;
      }

      // Submit
      await submitReport(entry.id, user.id, reason, details);

      // Check if this report crosses the threshold and notify admin
      const newCount = (entry.report_count || 0) + 1;
      if (newCount >= AUTO_HIDE_THRESHOLD) {
        try {
          await fetch('/api/emails', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              entryId: entry.id,
              entryTitle: entry.title,
              reportCount: newCount,
              submitter: entry.profiles?.username,
            }),
          });
        } catch {
          // Email failure is non-critical, don't block the UX
        }
      }

      setStep('done');
      onReported && onReported();
    } catch (e) {
      if (e.message?.includes('unique') || e.code === '23505') {
        setErrorMsg('You have already reported this entry.');
      } else {
        setErrorMsg(e.message || 'Something went wrong. Please try again.');
      }
      setStep('error');
    }
    setSubmitting(false);
  };

  return (
    <div style={s.overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={s.modal}>
        {/* Header */}
        <div style={s.header}>
          <span style={s.title}>
            {step === 'done'  ? 'Report submitted'   :
             step === 'error' ? 'Cannot submit report' :
             'Report this entry'}
          </span>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        {/* Pick reason */}
        {step === 'pick' && (
          <div style={s.body}>
            <div style={{ fontSize: 12.5, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.6 }}>
              Help us keep CopyPasteGenius safe. Reports are anonymous to the entry owner.
              After {AUTO_HIDE_THRESHOLD} reports, content is automatically hidden pending admin review.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 7, marginBottom: 16 }}>
              {REASONS.map(r => (
                <div
                  key={r.id}
                  onClick={() => setReason(r.id)}
                  style={{
                    border: `0.5px solid ${reason === r.id ? 'var(--accent-border)' : 'var(--border2)'}`,
                    background: reason === r.id ? 'var(--accent-dim)' : 'var(--surface2)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '10px 14px',
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                  <div style={{
                    width: 16, height: 16, borderRadius: '50%', flexShrink: 0, marginTop: 1,
                    border: `1.5px solid ${reason === r.id ? 'var(--accent)' : 'var(--border3)'}`,
                    background: reason === r.id ? 'var(--accent)' : 'transparent',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {reason === r.id && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 500, marginBottom: 2 }}>{r.label}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--muted)' }}>{r.desc}</div>
                  </div>
                </div>
              ))}
            </div>

            {reason === 'other' && (
              <div style={{ marginBottom: 14 }}>
                <textarea
                  placeholder="Please describe the issue..."
                  value={details}
                  onChange={e => setDetails(e.target.value)}
                  style={{
                    width: '100%', background: 'var(--bg)',
                    border: '0.5px solid var(--border2)', borderRadius: 'var(--radius-sm)',
                    padding: '9px 12px', color: 'var(--text)', fontFamily: 'var(--font)',
                    fontSize: 13, outline: 'none', resize: 'vertical', minHeight: 70,
                    lineHeight: 1.6,
                  }}
                />
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={s.cancelBtn}>Cancel</button>
              <button
                onClick={handleSubmit}
                disabled={!reason || submitting}
                style={{
                  ...s.submitBtn,
                  opacity: (!reason || submitting) ? 0.5 : 1,
                  cursor: (!reason || submitting) ? 'not-allowed' : 'pointer',
                }}>
                {submitting ? 'Submitting...' : 'Submit report'}
              </button>
            </div>
          </div>
        )}

        {/* Done */}
        {step === 'done' && (
          <div style={{ ...s.body, textAlign: 'center', padding: '28px 24px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✓</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8 }}>
              Thank you for the report
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 20 }}>
              Our team will review this content. If it violates our guidelines it will be removed.
            </div>
            <button onClick={onClose} style={s.submitBtn}>Done</button>
          </div>
        )}

        {/* Error */}
        {step === 'error' && (
          <div style={{ ...s.body, textAlign: 'center', padding: '28px 24px' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>⚠</div>
            <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text)', marginBottom: 8 }}>
              Report not submitted
            </div>
            <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 20 }}>
              {errorMsg}
            </div>
            <button onClick={onClose} style={s.cancelBtn}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 16,
  },
  modal: {
    background: 'var(--surface)', border: '0.5px solid var(--border2)',
    borderRadius: 'var(--radius-lg)', width: '100%', maxWidth: 420,
    maxHeight: '90vh', overflow: 'auto', fontFamily: 'var(--font)',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', borderBottom: '0.5px solid var(--border)',
  },
  title: { fontSize: 14, fontWeight: 500, color: 'var(--text)' },
  closeBtn: {
    background: 'none', border: 'none', color: 'var(--dim)',
    fontSize: 20, cursor: 'pointer', lineHeight: 1, padding: 2,
  },
  body: { padding: '18px 20px' },
  cancelBtn: {
    background: 'none', border: '0.5px solid var(--border2)',
    borderRadius: 'var(--radius-sm)', padding: '8px 16px',
    fontFamily: 'var(--font)', fontSize: 13, color: 'var(--muted)', cursor: 'pointer',
  },
  submitBtn: {
    background: 'var(--danger)', color: '#fff', border: 'none',
    borderRadius: 'var(--radius-sm)', padding: '8px 18px',
    fontFamily: 'var(--font)', fontSize: 13, fontWeight: 500, cursor: 'pointer',
  },
};